import { BlockBlobClient } from '@azure/storage-blob';
import { ImageEditOptions } from '@sdir/blueprint';
import {
  AttachApplicationAttachmentResult,
  AttachmentResult,
  AttachmentType,
  GetApplicationsQueryResult,
  RequestReadResult,
} from '@sdir/httpclient/lib/clients/aps/application';
import { AxiosResponse } from 'axios';
import { applicationAttachmentApi } from '../httpclient';
import { AttachmentUpdate, NewAttachment } from '../types';
import { BaseTracker, SubmitItem, SubmitStatus } from '../types/submitSequence';
import { addToSequence } from './submitSequenceHelpers';

// Should match with implementation in APS
type AttachmentMetadata = {
  imageEditOptions?: ImageEditOptions | null;
};

type AttachmentExtras = {
  group?: string | null;
  metadata?: AttachmentMetadata | null;
  simulatorResultItemUri?: string | null;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const parseMetadata = (attachment: AttachmentResult): AttachmentMetadata | null => {
  if (attachment.metadata) return JSON.parse(attachment.metadata) as AttachmentMetadata;
  return null;
};

export const uploadAddedApplicationFile = async (
  applicationUid: string,
  file: File,
  attachmentType: AttachmentType,
  extras?: AttachmentExtras | null,
  progressListener?: (progressPercent: number) => void
): Promise<AxiosResponse<AttachApplicationAttachmentResult>> => {
  const tokenResponse = await applicationAttachmentApi.v1AttachmentGetuploadtokenRequestPost({
    applicationUid,
    filename: file.name,
    type: attachmentType,
  });
  if (!tokenResponse.data?.blobUrl) {
    throw new Error('Unable to receive file upload token');
  }
  const blockBlobClient = new BlockBlobClient(tokenResponse.data.blobUrl);
  await blockBlobClient.upload(file, file.size, {
    blobHTTPHeaders: {
      blobContentType: file.type,
    },
    onProgress: (p) => {
      if (progressListener) {
        const progressPercent = !file.size ? 100 : p.loadedBytes / file.size;
        progressListener(progressPercent);
      }
    },
  });
  const attachResponse =
    await applicationAttachmentApi.v1AttachmentAttachtoownapplicationCommandPost({
      applicationUid,
      blobName: file.name,
      type: attachmentType,
      group: extras?.group,
      metadata: extras?.metadata ? JSON.stringify(extras.metadata) : null,
      simulatorResultItemUri: extras?.simulatorResultItemUri,
    });
  return attachResponse;
};

export const getAttachmentUrl = async (applicationUid: string, attachment: AttachmentResult) => {
  const temp = await applicationAttachmentApi.v1AttachmentGetdownloadurlRequestPost({
    attachmentUid: attachment.uid,
    blobName: attachment.blobName,
    containerUid: applicationUid,
  });
  // incorrect API spec
  const tokenResponse = temp as any as AxiosResponse<RequestReadResult>;
  if (!tokenResponse.data?.blobUrl) {
    throw new Error('Unable to receive file upload token');
  }
  return tokenResponse.data?.blobUrl;
};

export const downloadApplicationFile = async (
  applicationUid: string,
  attachment: AttachmentResult
) => {
  const temp = await applicationAttachmentApi.v1AttachmentGetdownloadurlRequestPost({
    attachmentUid: attachment.uid,
    blobName: attachment.blobName,
    containerUid: applicationUid,
  });
  // incorrect API spec
  const tokenResponse = temp as any as AxiosResponse<RequestReadResult>;
  if (!tokenResponse.data?.blobUrl) {
    throw new Error('Unable to receive file upload token');
  }
  const blockBlobClient = new BlockBlobClient(tokenResponse.data.blobUrl);
  const downloadResponse = await blockBlobClient.download();
  const blob = await downloadResponse.blobBody;
  if (!blob) {
    throw new Error('Empty blob data');
  }
  return new File([blob], attachment.blobName || 'unknown.dat', { type: blob.type });
};

export const removeApplicationFile = async (
  applicationUid: string,
  attachment: AttachmentResult
): Promise<AxiosResponse> => {
  return applicationAttachmentApi.v1AttachmentDeleteownattachmentAttachmentIdDelete(
    applicationUid,
    attachment.uid || ''
  );
};

export const makeUniqueFilename = (filename: string, usedFilenames: Set<string>) => {
  if (!usedFilenames.has(filename)) {
    return filename;
  }
  // matches other file -> create with new name using counter
  const fileNameParts = filename.split('.');
  const namePart =
    fileNameParts.length > 0 ? fileNameParts.slice(0, -1).join('.') : fileNameParts[0];
  const extensionPart =
    fileNameParts.length > 0 ? `.${fileNameParts[fileNameParts.length - 1]}` : '';
  let fileCount = 2;
  let newFileName = `${namePart}(${fileCount})${extensionPart}`;
  // Avoid duplicates for case w/ files like: scan.pdf + scan(2).pdf + scan.pdf
  // => scan.pdf, scan(2).pdf, scan(3).pdf
  while (usedFilenames.has(newFileName)) {
    fileCount += 1;
    newFileName = `${namePart}(${fileCount})${extensionPart}`;
  }
  return newFileName;
};

export const makeFilenameUnique = (file: File, usedFilenames: Set<string>) => {
  if (!usedFilenames.has(file.name)) return file;
  const uniqueFilename = makeUniqueFilename(file.name, usedFilenames);
  return new File([file], uniqueFilename, { type: file.type });
};

type BaseFileAttachment = { file: File; attachmentType: AttachmentType };
export const makeFilenamesUnique = <T extends BaseFileAttachment = BaseFileAttachment>(
  files: T[],
  existingAttachments?: AttachmentResult[] | null
): T[] => {
  const usedFilenames = new Set<string>();
  if (existingAttachments) {
    existingAttachments.forEach((a) => usedFilenames.add(a.blobName || ''));
  }
  return files.map((x) => {
    const file = makeFilenameUnique(x.file, usedFilenames);
    usedFilenames.add(file.name);
    return { ...x, file };
  });
};

export type SubmitAttachmentUpdatesTracker = BaseTracker & {
  newAttachments?: SubmitStatus<AttachApplicationAttachmentResult>[];
  deletedAttachments?: SubmitStatus[];
};

export type SubmitAttachmentUpdatesParams = {
  application: GetApplicationsQueryResult;
  newAttachments: NewAttachment[];
  removedAttachments: string[];
  updatedAttachments: AttachmentUpdate[];
  attachmentGroup: string | null;
  qualificationUri?: string | null;
};

export const buildSubmitAttachmentUpdatesSequence = <T extends SubmitAttachmentUpdatesTracker>({
  newAttachments,
  removedAttachments,
  updatedAttachments,
  application,
  attachmentGroup,
  qualificationUri,
}: SubmitAttachmentUpdatesParams): SubmitItem<T>[] => {
  const sequence: SubmitItem<T>[] = [];

  const usedFilenames = new Set<string>();
  if (application.attachments) {
    application.attachments.forEach((a) => usedFilenames.add(a.blobName || ''));
  }

  const attachmentsToAdd = newAttachments.concat(
    updatedAttachments.map((u) => {
      if (!u.newFile || !u.newType) throw new Error('Incomplete updated attachment');
      return { file: u.newFile, type: u.newType };
    })
  );

  const attachmentsToRemove = removedAttachments
    .concat(updatedAttachments.map((u) => u.attachmentUid))
    .map((attachmentUid) => application.attachments?.find((a) => a.uid === attachmentUid))
    .filter((a): a is AttachmentResult => !!a);

  attachmentsToRemove.forEach((attachment, addIndex) => {
    addToSequence(sequence, {
      labelId: 'submitsteps.fileremoval',
      formatParams: { filename: attachment.blobName },
      apiCall: () => removeApplicationFile(application.uid || '', attachment),
      statusSelector: (ctx) => ctx.deletedAttachments?.[addIndex],
      statusSetter: (ctx, status) => ({
        ...ctx,
        deletedAttachments: [
          ...(ctx.deletedAttachments || []).slice(0, addIndex),
          status,
          ...(ctx.deletedAttachments || []).slice(addIndex + 1),
        ],
      }),
    });
  });

  attachmentsToAdd.forEach(({ file, type }, addIndex) => {
    addToSequence(sequence, {
      labelId: 'submitsteps.fileupload',
      formatParams: { filename: file.name },
      apiCall: () => {
        const fileUniqueName = makeFilenameUnique(file, usedFilenames);
        usedFilenames.add(fileUniqueName.name);
        return uploadAddedApplicationFile(application.uid || '', fileUniqueName, type, {
          group: attachmentGroup,
          simulatorResultItemUri: qualificationUri,
        });
      },
      statusSelector: (ctx) => ctx.newAttachments?.[addIndex],
      statusSetter: (ctx, status) => ({
        ...ctx,
        newAttachments: [
          ...(ctx.newAttachments || []).slice(0, addIndex),
          status,
          ...(ctx.newAttachments || []).slice(addIndex + 1),
        ],
      }),
    });
  });

  return sequence;
};

export type CreateAttachmentGroupTracker = BaseTracker & {
  addedAttachments?: { [name: string]: SubmitStatus<AttachApplicationAttachmentResult> };
};

export type CreateAttachmentGroupParams = {
  application: GetApplicationsQueryResult;
  files: File[];
  attachmentType: AttachmentType;
  simulatorResultItemUri: string | null | undefined;
  progressListener?: (progress: number) => void;
};
export const buildCreateAttachmentGroupSequence = ({
  files,
  attachmentType,
  application,
  simulatorResultItemUri,
  progressListener,
}: CreateAttachmentGroupParams): SubmitItem<CreateAttachmentGroupTracker>[] => {
  const usedFilenames = new Set<string>();
  if (application.attachments) {
    application.attachments.forEach((a) => usedFilenames.add(a.blobName || ''));
  }

  return files.map((file) => {
    const fileUniqueName = makeFilenameUnique(file, usedFilenames);
    usedFilenames.add(fileUniqueName.name);
    return {
      labelId: 'submitsteps.fileupload',
      formatParams: { filename: file.name },
      apiCall: (ctx) => {
        const attachmentGroup = Object.values(ctx.addedAttachments || {})[0]?.result?.group;
        return uploadAddedApplicationFile(
          application.uid || '',
          fileUniqueName,
          attachmentType,
          {
            group: attachmentGroup,
            simulatorResultItemUri,
          },
          progressListener
        );
      },
      statusSelector: (ctx) => ctx.addedAttachments?.[fileUniqueName.name],
      statusSetter: (ctx, status) => ({
        ...ctx,
        addedAttachments: {
          ...ctx.addedAttachments,
          [fileUniqueName.name]: status,
        },
      }),
    };
  });
};
